import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import {
    Component,
    DestroyRef,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { RollForward } from '../../classes/roll-forward';
import { RollForwardTable } from '../../classes/roll-forward-table';
import { AppService } from '../../services/app.service';
import { SettingsService } from '../../services/settings.service';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { UserInfo, UserService } from '../../services/user.service';
import { SchoolYearPipe } from '../../pipes/schoolyear/schoolyear.pipe';
import { SelectionCountPipe } from '../../pipes/selectioncount/selectioncount.pipe';
import { ErrorService } from '../../services/error.service';

@Component({
    selector: 'app-roll-forward',
    templateUrl: './roll-forward.component.html',
    styleUrls: ['./roll-forward.component.scss'],
    imports: [
        FormsModule,
        MatInputModule,
        CommonModule,
        SchoolYearPipe,
        MatCheckboxModule,
        MatTableModule,
        SelectionCountPipe,
        MatIconModule,
        MatCardModule,
        MatDividerModule,
        MatButtonModule
    ]
})
export class RollForwardComponent implements OnInit {
    protected readonly destroyRef = inject(DestroyRef);
    protected readonly appSvc = inject(AppService);
    protected readonly confirmDlgSvc = inject(ConfirmationDialogService);
    protected readonly settingSvc = inject(SettingsService);
    protected readonly snackBarSvc = inject(SnackBarService);
    protected readonly userSvc = inject(UserService);
    protected readonly errorSvc = inject(ErrorService);
    user: UserInfo;
    dataSource: MatTableDataSource<RollForwardTable>;
    @ViewChild(MatSort) sort: MatSort;
    displayedColumns: string[] = [
        'Select',
        'LatestActiveYear',
        'ScholarshipName',
        'Active',
        'MasterID',
        'ScholarshipID',
    ];
    selection = new SelectionModel<RollForwardTable>(true, []);
    scholarships: RollForwardTable[];
    fromYear: string;
    endYear: string;
    valid: boolean;
    error: string[] = [];
    activeLastYearCount: number;
    historicallyActiveCount: number;
    notActiveCount: number;
    alreadyRolledCount: number;

    ngOnInit() {
        this.user = this.userSvc.User;
        this.getTableData();
        this.getSettings();
    }

    getSettings() {
        this.settingSvc.SettingsSubject.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((settings) => {
            if (settings == null) return;
            this.fromYear = settings.find(
                (f) => f.Label === 'Rollover From Year'
            ).Value;
            this.endYear = settings.find(
                (f) => f.Label === 'School year (scholarship admin)'
            ).Value;
            let startDate = settings.find(
                (f) => f.Label === 'Scholarship app start date'
            ).Value;
            let endDate = settings.find(
                (f) => f.Label === 'Scholarship app end date'
            ).Value;
            let rolloverAvailable =
                settings.find((f) => f.Label === 'Rollover Available').Value ===
                '1';
            let applicationNotInProgress = new Date() < new Date(startDate) || new Date() > new Date(endDate);
            let yearsInOrder = this.fromYear < this.endYear;
            this.valid = true;
            //rolloverAvailable && applicationNotInProgress && yearsInOrder;

            if (!rolloverAvailable)
                this.error.push('Rollover setting not enabled');
            if (!applicationNotInProgress)
                this.error.push('Current year`s applications are still active');
            if (!yearsInOrder)
                this.error.push('From year is greater than end year');
        });
    }

    getTableData() {
        this.appSvc
            .GetRollForwardTable()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (res) => {
                    this.dataSource = new MatTableDataSource(res);
                    this.dataSource.sort = this.sort;
                    this.scholarships = res;
                    this.activeLastYearCount = res.filter(
                        (f) => f.ActiveLastYear
                    ).length;
                    this.historicallyActiveCount = res.filter(
                        (f) => f.HistoricallyActive
                    ).length;
                    this.notActiveCount = res.filter((f) => f.NotActive).length;
                    this.alreadyRolledCount = res.filter(
                        (f) => f.AlreadyRolled
                    ).length;
                },
                error: (error) => {
                    this.errorSvc.HandleServiceError(error);
                }
            });
    }

    selectActiveLastYear() {
        let lastActive = this.scholarships.filter(
            (f) => f.LatestActiveYear === this.fromYear && f.Active
        );
        this.selection.select(...lastActive);
    }

    rollForward() {
        if (this.selection.selected.length === 0) {
            this.snackBarSvc.error(
                'Please select scholarships to roll forward'
            );
            return;
        }

        let schList = this.selection.selected.map<string>(
            (m) => '<br>' + m.ScholarshipName
        );

        let dialog = this.confirmDlgSvc.Open({
            disableClose: true,
            TitleText: 'Are you sure?',
            MessageText:
                'The following scholarships will be rolled forward:<br>' +
                schList,
            OKText: 'Roll Forward',
            CancelText: 'Cancel',
        });

        dialog.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
            if (res === true) {
                let dto: RollForward = {
                    Scholarships: this.selection.selected.map<number>(
                        (m) => m.ScholarshipID
                    ),
                };

                this.appSvc
                    .RollForward(dto)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: (res) => {
                            this.snackBarSvc.success(
                                'Scholarships Successfully Rolled Forward'
                            ),
                                10000;
                            this.dataSource = new MatTableDataSource(res);
                            this.dataSource.sort = this.sort;
                            this.scholarships = res;
                            this.activeLastYearCount = res.filter(
                                (f) => f.ActiveLastYear
                            ).length;
                            this.historicallyActiveCount = res.filter(
                                (f) => f.HistoricallyActive
                            ).length;
                            this.notActiveCount = res.filter(
                                (f) => f.NotActive
                            ).length;
                            this.alreadyRolledCount = res.filter(
                                (f) => f.AlreadyRolled
                            ).length;
                        },
                        error: (error) => {
                            this.errorSvc.HandleServiceError(error);
                        }
                    });
            }
        });
    }
}
